import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useOutletContext } from "react-router-dom";
import {
  evalApi,
  fetchFailedCases,
  type FailedCase,
} from "../../client/EvalApiClient";
import { fetchUsageMetrics, type UsageMetrics } from "../../client/UsageAnalyticsClient";
import { getOfflineEvalMetrics, type OfflineEvalMetrics } from "./offlineMetricsApi";
import "./EvalHomePage.css";
import { DEFAULT_EVAL_AGENT, evalAgentLabel } from "../../agents";

// ─── Types ──────────────────────────────────────────────────────────────────

interface StatCard {
  label: string;
  value: string;
  sub?: string;
}

// ─── Filter config ──────────────────────────────────────────────────────────

const FILTERS = ["Today", "7D", "30D", "3M"];

const FILTER_LABEL_KEYS: Record<string, `home.filters.${"today" | "d7" | "d30" | "m3"}`> = {
  Today: "home.filters.today",
  "7D": "home.filters.d7",
  "30D": "home.filters.d30",
  "3M": "home.filters.m3",
};

/** Map filter label to query parameter value (days or keyword). */
const FILTER_TO_PERIOD: Record<string, string> = {
  Today: "1d",
  "7D": "7d",
  "30D": "30d",
  "3M": "90d",
};

/** Convert a period string to concrete start/end date range. */
function periodToDateRange(period: string): { startDate: string; endDate: string } {
  const end = new Date();
  const endStr = end.toISOString().slice(0, 10);
  const daysMap: Record<string, number> = { "1d": 1, "7d": 7, "30d": 30, "90d": 90 };
  const days = daysMap[period] ?? 7;
  const start = new Date(end.getTime() - days * 86_400_000);
  return { startDate: start.toISOString().slice(0, 10), endDate: endStr };
}


interface EvalOutletContext {
  agent: string;
}

// The pre-deployment (offline) metrics are platform-wide, not per agent: shown on
// the default agent's page only, so they are not read as another agent's numbers.
const OFFLINE_METRICS_AGENT = DEFAULT_EVAL_AGENT;

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatNumber(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return n.toLocaleString("en-US");
  return String(n);
}

function formatMs(ms: number): string {
  if (!ms || ms <= 0) return "—";
  const sec = ms / 1000;
  if (sec < 60) return `${sec.toFixed(1)}s`;
  return `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;
}

/** Tone for an Online stat value — Error red, Pass green, otherwise neutral. */
function statTone(label: string): "danger" | "success" | "" {
  const l = label.toLowerCase();
  if (l.includes("error")) return "danger";
  if (l.includes("pass")) return "success";
  return "";
}

type FailKey = "home.statsFailed" | "home.usageFailed" | "home.metricsFailed" | "home.failuresFailed";

/** A load failure: the error's own message, or a fallback key translated at render. */
type LoadError = { message: string } | { key: FailKey } | null;

function loadError(e: unknown, key: FailKey): LoadError {
  const message = (e as { message?: unknown } | null)?.message;
  return typeof message === "string" && message ? { message } : { key };
}

function ErrorText({ error }: { error: NonNullable<LoadError> }) {
  const { t } = useTranslation("eval");
  return <p className="eval-home__error">{"message" in error ? error.message : t(error.key)}</p>;
}

function IconSpinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="eval-home__spinner">
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function EvalHomePage() {
  const { t } = useTranslation("eval");
  const { agent } = useOutletContext<EvalOutletContext>();
  const [activeFilter, setActiveFilter] = useState("7D");
  const [stats, setStats] = useState<StatCard[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<LoadError>(null);

  const period = FILTER_TO_PERIOD[activeFilter] || "30d";
  // Online stats and failed cases are requested for the selected agent (?agent=).
  const agentLabel = evalAgentLabel(agent);

  // `isStale` is true once a newer request (another agent / period) has started, so a
  // slow earlier answer never overwrites the current one.
  const fetchStats = useCallback(async (p: string, isStale: () => boolean) => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const data = await evalApi.getHomeStats(p, agent);
      if (!isStale()) setStats(data.stats || []);
    } catch (e) {
      if (!isStale()) {
        setStatsError(loadError(e, "home.statsFailed"));
        setStats([]);
      }
    } finally {
      if (!isStale()) setStatsLoading(false);
    }
  }, [agent]);

  useEffect(() => {
    let cancelled = false;
    fetchStats(period, () => cancelled);
    return () => { cancelled = true; };
  }, [period, fetchStats]);

  return (
    <div className="eval-home">
      {/* Header: title + date-range + period filters.
          (Breadcrumb + agent selector are provided by EvalLayout.) */}
      <div className="eval-home__header">
        <h1 className="eval-home__title">{t("agentTitle", { agent: agentLabel })}</h1>
        <div className="eval-home__controls">
          <div className="eval-home__filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                className={"eval-home__filter-btn" + (activeFilter === f ? " eval-home__filter-btn--active" : "")}
                onClick={() => setActiveFilter(f)}
              >
                {t(FILTER_LABEL_KEYS[f])}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ─── Overview: Usage (left) | Online + Offline (right) ─── */}
      <div className="eval-home__overview">
        <UsageSection agent={agent} period={period} />
        <div className="eval-home__side">
          <OnlineCard
            stats={stats}
            loading={statsLoading}
            error={statsError}
          />
          <OfflineCard period={period} enabled={agent === OFFLINE_METRICS_AGENT} />
        </div>
      </div>

      {/* ─── Error Log ─── */}
      <div className="eval-home__panel">
        <ErrorLogTable agent={agent} />
      </div>
    </div>
  );
}

// ─── Usage (fetchUsageMetrics) ───────────────────────────────────────────────

function UsageSection({ agent, period }: { agent: string; period: string }) {
  const { t } = useTranslation("eval");
  const [data, setData] = useState<UsageMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<LoadError>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const { startDate, endDate } = periodToDateRange(period);
    fetchUsageMetrics(agent, 'custom', startDate, endDate)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) { setError(loadError(e, "home.usageFailed")); setData(null); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [agent, period]);

  const latency = data?.responseLatency;
  const kpis: StatCard[] = data
    ? [
        { label: t("home.activeUsers"), value: formatNumber(data.activeUsers) },
        { label: t("home.sessions"), value: formatNumber(data.sessions) },
        {
          label: t("home.messages"),
          value: formatNumber(data.messages),
          sub: t("home.toolCalls", { n: formatNumber(data.toolCalls) }),
        },
        {
          label: t("home.tokens"),
          value: formatNumber(data.inputTokens + data.outputTokens),
          sub: t("home.tokensSplit", { input: formatNumber(data.inputTokens), output: formatNumber(data.outputTokens) }),
        },
        ...(latency && latency.sampleCount > 0
          ? [{ label: t("home.latency"), value: `${formatMs(latency.avgMs)} / ${formatMs(latency.p95Ms)}` }]
          : []),
      ]
    : [];

  const maxChannel = Math.max(1, ...(data?.channelDistribution ?? []).map((c) => c.count));

  return (
    <div className="eval-home__panel eval-home__panel--usage">
      <div className="eval-home__section-head">
        <h2 className="eval-home__section-title">{t("home.usage")}</h2>
        <Link to="/eval/usage" className="eval-home__section-link">{t("home.usageLink")}</Link>
      </div>

      {loading ? (
        <div className="eval-home__loading"><IconSpinner /> {t("home.loadingUsage")}</div>
      ) : error ? (
        <ErrorText error={error} />
      ) : !data ? (
        <p className="eval-home__empty">{t("home.noUsage")}</p>
      ) : (
        <>
          <div className="eval-home__usage-kpis">
            {kpis.map((k) => (
              <div key={k.label} className="eval-home__usage-kpi">
                <p className="eval-home__card-label">{k.label}</p>
                <p className="eval-home__usage-kpi-value">{k.value}</p>
                {k.sub && <p className="eval-home__card-sub">{k.sub}</p>}
              </div>
            ))}
          </div>

          <div className="eval-home__usage-channels">
            <p className="eval-home__failure-block-title">{t("home.channels")}</p>
            {data.channelDistribution.length === 0 ? (
              <p className="eval-home__empty">{t("home.noChannels")}</p>
            ) : (
              data.channelDistribution.map((ch) => (
                <div key={ch.channel} className="eval-home__channel-row">
                  <span className="eval-home__channel-name">{ch.channel}</span>
                  <div className="eval-home__channel-bar-bg">
                    <div className="eval-home__channel-bar-fill" style={{ width: `${(ch.count / maxChannel) * 100}%` }} />
                  </div>
                  <span className="eval-home__channel-count">{formatNumber(ch.count)}</span>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Online card (/home/stats) ───────────────────────────────────────────────

function OnlineCard({
  stats,
  loading,
  error,
}: {
  stats: StatCard[];
  loading: boolean;
  error: LoadError;
}) {
  const { t } = useTranslation("eval");
  return (
    <div className="eval-home__panel">
      <div className="eval-home__section-head">
        <h2 className="eval-home__section-title">{t("home.online")}</h2>
        <Link to="/eval/reports?tab=continuous" className="eval-home__section-link">{t("home.viewAll")}</Link>
      </div>
      {loading ? (
        <div className="eval-home__loading"><IconSpinner /> {t("home.loadingStats")}</div>
      ) : error ? (
        <ErrorText error={error} />
      ) : stats.length === 0 ? (
        <p className="eval-home__empty">{t("home.noStats")}</p>
      ) : (
        <div className="eval-home__stat-grid">
          {stats.map((card) => {
            const tone = statTone(card.label);
            return (
              <div key={card.label} className="eval-home__stat">
                <p className="eval-home__card-label">{card.label}</p>
                <p className={"eval-home__stat-value" + (tone ? ` eval-home__stat-value--${tone}` : "")}>
                  {card.value}
                </p>
                {card.sub && <p className="eval-home__card-sub">{card.sub}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Offline card (GetOfflineEvalMetrics) ─────────────────────────────────────

function OfflineCard({ period, enabled }: { period: string; enabled: boolean }) {
  const { t } = useTranslation("eval");
  const [metrics, setMetrics] = useState<OfflineEvalMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<LoadError>(null);

  useEffect(() => {
    if (!enabled) { setMetrics(null); setLoading(false); setError(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    // The backend owns the window + aggregation now (no client-side reduce over
    // a capped 100-run page): promotion is bucketed by gate verdict, integration
    // reported as pass/fail.
    getOfflineEvalMetrics(period)
      .then((m) => { if (!cancelled) setMetrics(m); })
      .catch((e) => { if (!cancelled) { setError(loadError(e, "home.metricsFailed")); setMetrics(null); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, enabled]);

  const promotion = metrics?.promotion;
  const integration = metrics?.integration;
  // "No runs" = neither promotion nor integration runs in the window.
  const hasRuns = !!metrics && (promotion!.total > 0 || integration!.total > 0);

  return (
    <div className="eval-home__panel">
      <div className="eval-home__section-head">
        <h2 className="eval-home__section-title">{t("home.offline")}</h2>
        <Link to="/eval/reports?tab=pre-deployment" className="eval-home__section-link">{t("home.viewAll")}</Link>
      </div>
      {!enabled ? (
        <p className="eval-home__empty">{t("home.noEvalData")}</p>
      ) : loading ? (
        <div className="eval-home__loading"><IconSpinner /> {t("home.loading")}</div>
      ) : error ? (
        <ErrorText error={error} />
      ) : !hasRuns ? (
        <p className="eval-home__empty">{t("home.noRuns")}</p>
      ) : (
        <>
          <div className="eval-home__stat-grid">
            <div className="eval-home__stat">
              <p className="eval-home__card-label">{t("home.avgPassRate")}</p>
              <p className="eval-home__stat-value">
                {promotion!.scoredRuns > 0 ? `${promotion!.avgPassRate.toFixed(0)}%` : "—"}
              </p>
              <p className="eval-home__card-sub">
                {t(promotion!.scoredRuns === 1 ? "home.acrossOne" : "home.acrossMany", { n: promotion!.scoredRuns })}
              </p>
            </div>
            <div className="eval-home__stat">
              <p className="eval-home__card-label">{t("home.promotionRate")}</p>
              <p className="eval-home__stat-value">
                {promotion!.promotionRate != null ? `${promotion!.promotionRate.toFixed(0)}%` : "—"}
              </p>
              {/* Rate counts promoted + skipped as "released", but the breakdown
                  keeps them distinct: promoted = cleared the gate on merit,
                  skipped = matched no test cases (released without a gate),
                  blocked = failed the gate. */}
              <p className="eval-home__card-sub eval-home__gate-breakdown">
                <span className="eval-home__gate-dot eval-home__gate-dot--promoted" />
                {t("home.promoted", { n: promotion!.promoted })}
                <span className="eval-home__gate-dot eval-home__gate-dot--skipped" />
                {t("home.skipped", { n: promotion!.skipped })}
                <span className="eval-home__gate-dot eval-home__gate-dot--blocked" />
                {t("home.blocked", { n: promotion!.blocked })}
              </p>
            </div>
          </div>
          {/* Integration-test runs are a separate track (no gate — a binary
              pass/fail). Show a compact line only when there are any, so the
              split the backend now returns is visible without cluttering the
              card for the common promotion-only case. */}
          {integration!.total > 0 && (
            <p className="eval-home__card-sub eval-home__offline-integration">
              {t(integration!.running > 0 ? "home.integrationRunning" : "home.integration", {
                passed: integration!.passed,
                total: integration!.passed + integration!.failed,
                running: integration!.running,
              })}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ─── Error Log table (fetchFailedCases) ──────────────────────────────────────

const PAGE_SIZE = 8;

function ErrorLogTable({ agent }: { agent: string }) {
  const { t } = useTranslation("eval");
  const [cases, setCases] = useState<FailedCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<LoadError>(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchFailedCases(undefined, agent)
      .then((r) => { if (!cancelled) setCases(r.failed_cases || []); })
      .catch((e) => { if (!cancelled) { setError(loadError(e, "home.failuresFailed")); setCases([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [agent]);

  // Map a failed case to the Name / Failure Reason / Category columns.
  const rows = useMemo(
    () =>
      cases.map((c) => ({
        name: (c.evaluator || "").replace("Builtin.", "") || c.session_id || "—",
        reason: c.root_cause || c.evaluator_explanation || c.verdict || "—",
        category: c.severity || "—",
        key: `${c.session_id}-${c.trace_id}-${c.evaluator}`,
      })),
    [cases],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.reason.toLowerCase().includes(q) ||
        r.category.toLowerCase().includes(q),
    );
  }, [rows, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const clampedPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((clampedPage - 1) * PAGE_SIZE, clampedPage * PAGE_SIZE);

  return (
    <>
      <div className="eval-home__section-head">
        <h2 className="eval-home__section-title">
          {t("home.errorLog")} <span className="eval-home__section-note">{filtered.length}</span>
        </h2>
        <Link to="/eval/insights" className="eval-home__section-link">{t("home.insightsLink")}</Link>
      </div>

      {loading ? (
        <div className="eval-home__loading"><IconSpinner /> {t("home.loadingFailures")}</div>
      ) : error ? (
        <ErrorText error={error} />
      ) : cases.length === 0 ? (
        <p className="eval-home__empty">{t("home.noFailures")}</p>
      ) : (
        <>
          <div className="eval-home__table-toolbar">
            <input
              className="eval-home__search"
              type="search"
              placeholder={t("home.search")}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              aria-label={t("home.searchLabel")}
            />
            <div className="eval-home__pager">
              <button
                className="eval-home__pager-btn"
                disabled={clampedPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label={t("home.prevPage")}
              >
                ‹
              </button>
              <span className="eval-home__pager-info">{clampedPage} / {totalPages}</span>
              <button
                className="eval-home__pager-btn"
                disabled={clampedPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label={t("home.nextPage")}
              >
                ›
              </button>
            </div>
          </div>

          <table className="eval-home__table">
            <thead>
              <tr>
                <th className="eval-home__th">{t("home.colName")}</th>
                <th className="eval-home__th">{t("home.colReason")}</th>
                <th className="eval-home__th eval-home__th--category">{t("home.colCategory")}</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td className="eval-home__td" colSpan={3}>
                    <span className="eval-home__empty">{t("home.noMatch")}</span>
                  </td>
                </tr>
              ) : (
                pageRows.map((r) => (
                  <tr key={r.key} className="eval-home__tr">
                    <td className="eval-home__td eval-home__td--name">{r.name}</td>
                    <td className="eval-home__td eval-home__td--reason">
                      <span className="eval-home__fail-dot" aria-hidden="true" />
                      {r.reason}
                    </td>
                    <td className="eval-home__td">
                      <span className="eval-home__category-tag">{r.category}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}
